/* eslint-disable max-lines */
import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
  ActionResult,
  Challenge,
  ChoiceUserInputAction,
  ChoiceUserInputBranch,
  ChoiceUserInputBranchState,
  ChallengeMode,
  PitchRange,
  SpeechSpeed,
  UserInputAction,
} from "../../../schema";
import {minutesFromDeltaTimestamp} from "../../../utils";
import {isChoiceUserInputAction, isUserInputAction} from "../../../types";

import SessionActionDisplay from "./SessionActionDisplay";

import cn from "classnames";
import classes from "./SessionUserInputActionDisplay.module.css";
import {
  AlertCircleOutlineIcon,
  CaretDown,
  CheckmarkFilledCircleIcon,
  CheckmarkIcon,
  ChevronForwardCircleIcon,
  CrossCircleIcon,
  CrossIcon,
  HelpCircleIcon,
  KeyIcon,
  PlayCircleIcon,
} from "../../../ui/icons";
import WithTooltip from "../../../ui/tooltip";
import {gql} from "@apollo/client";
import {phraseToText} from "../../Phrase";
import {useAudio} from "../../../providers/audio";
import IconButton from "../../../ui/iconbutton";
import {getActionEmotionsData} from "../../../utils/player/utils";

type props = React.ComponentProps<"div"> & {
  action: UserInputAction | ChoiceUserInputAction
  result: ActionResult,
  noSpeech?: boolean,
  customScore?: boolean,
  challenge: Pick<Challenge, "mode">
  hideExpectedText?: boolean,
}

type labelProps = React.ComponentProps<"div"> & {
  variant?: "warning" | "success" | "failed" | "info",
  icon: React.ReactNode,
  text: string,
}

const SessionUserInputActionDisplay = function ({
  action,
  result,
  noSpeech,
  customScore,
  className,
  challenge,
  hideExpectedText = false,
  ...props
}: props) {
  const {t} = useTranslation();

  const icon = useMemo(() => {
    if (result.partlyPassed) {
      return <CheckmarkFilledCircleIcon className={cn(classes.icon, classes.partlyPassed)} />;
    }

    if (
      customScore ||
      (isUserInputAction(action) &&
        ([ChallengeMode.CHOICE_BASED_STUDY].includes(challenge.mode)))
    ) {
      return <></>;
    }

    return result.passed
      ? <CheckmarkFilledCircleIcon className={cn(classes.icon, classes.passed)} />
      : <CrossCircleIcon className={cn(classes.icon, classes.failed)} />;
  }, [result, customScore, action, challenge]);

  const {audioManager} = useAudio();
  const recognizedTextPairs = useMemo(() => {
    return new Map(result.recognizedTextPairs as Array<[string, string]>)
  }, [result.recognizedTextPairs])

  const onPlayClick = useCallback((e: React.MouseEvent) => {
    if (!result.audio) {
      console.warn("Cannot play user record: no mediafile");
      return;
    }

    audioManager.getPlayback("results")!
      .play(result.audio)
      .catch(console.error);
  }, [audioManager, result.audio]);

  const selectedBranches = useMemo(() => {
    if (isChoiceUserInputAction(action)) {
      if (action.isMultipleChoice && result.branchIds)
        return action.branches.filter((branch) => result.branchIds?.includes(branch.id));
      if (result.branchId !== undefined) return action.branches.filter((branch) => branch.id === result.branchId);
    }

    return [];
  }, [action, result]);

  const selectedBranch = useMemo(() => selectedBranches.at(0), [selectedBranches]);

  const mainText = useMemo(() => {
    switch (action.__typename) {
      case "UserInputAction":
        if (action.freeSpeech && result.freeSpeechMessage) {
          return (
            <span>
              {result.freeSpeechMessage}
            </span>
          )
        }
        if (action.hintText !== "") {
          return (
            <span>
              {phraseToText(action.hintText)}
              <WithTooltip as={"span"} helpText={action.referenceText}><KeyIcon/></WithTooltip>
            </span>
          )
        }
        if (result.recognizedText) {
          if (hideExpectedText) {
            return (
              <div>
                <p className={classes.recognizedText}>{phraseToText(result.recognizedText)}</p>
              </div>
            )
          }
          return (
            <>
              <div>
                {t("components.SessionUserInputActionDisplay.referenceText") + ":"}
                <p className={classes.mainText}>{phraseToText(action.referenceText)}</p>
              </div>
              <div>
                {t("components.SessionUserInputActionDisplay.recognizedText") + ":"}
                <p className={classes.recognizedText}>{phraseToText(result.recognizedText)}</p>
              </div>
            </>
          )
        }
      return phraseToText(action.referenceText)
      case "ChoiceUserInputAction":
        if (action.isMultipleChoice) return t("components.SessionUserInputActionDisplay.multipleChoice");

        if (selectedBranch === undefined) {
          if (result.recognizedText)
            return (
              <div>
                {t("components.SessionUserInputActionDisplay.recognizedText") + ":"}
                <p className={classes.recognizedText}>{phraseToText(result.recognizedText)}</p>
              </div>
            );
          else return "";
        }
        if (result.recognizedText && selectedBranch.userInput.referenceText !== result.recognizedText) {
          return (
            <>
              <div>
                {t("components.SessionUserInputActionDisplay.answerOption") + ":"}
                <p className={classes.mainText}>{phraseToText(selectedBranch.userInput.referenceText)}</p>
              </div>
              <div>
                {t("components.SessionUserInputActionDisplay.recognizedText") + ":"}
                <p className={classes.recognizedText}>{phraseToText(result.recognizedText)}</p>
              </div>
            </>
          )
        }
        return phraseToText(selectedBranch.userInput.referenceText);
      default:
        throw new Error(`Trying to show result for unsupported User Action type: "${action.__typename}"`);
    }
  }, [action, selectedBranch, result, hideExpectedText, t]);

  const subItem = useMemo(() => {
    const getSubIcon = (branch: ChoiceUserInputBranch) =>
      customScore ? (
        <ChevronForwardCircleIcon className={classes.icon} />
      ) : branch.state === ChoiceUserInputBranchState.CORRECT ? (
        <CheckmarkFilledCircleIcon className={cn(classes.icon, classes.passed)} />
      ) : (
        <CrossCircleIcon className={cn(classes.icon, classes.failed)} />
      );

    if (isChoiceUserInputAction(action)) {
      if (customScore && result.isSkipped) {
        return (
          <span className={cn(classes.subText, classes.correctBranches)}>
            {t("components.SessionUserInputActionDisplay.choices")}
            {action.branches.map((branch) => (
              <span key={branch.id}>{phraseToText(branch.userInput.referenceText)}</span>
            ))}
          </span>
        );
      }

      const isFail =!result.passed && (
        selectedBranches.find((branch) => branch.state !== ChoiceUserInputBranchState.CORRECT) ||
        (action.isMultipleChoice &&
          !selectedBranches.find((branch) => branch.state !== ChoiceUserInputBranchState.CORRECT)));

      return (
        <span className={cn(classes.subText, classes.correctBranches)}>
          {action.isMultipleChoice &&
          (<div className={classes.reference}>
            <span>{t("components.SessionUserInputActionDisplay.answerOption")}</span>
            <span>&#126;{t("components.SessionUserInputActionDisplay.recognizedText")}</span>
          </div>)}
          {action.isMultipleChoice &&
            selectedBranches.map((branch) => (
              <div key={`selected-branch-${branch.id}`} className={classes.chosenBranches}>
                <div className={classes.firstLine}>
                  {getSubIcon(branch)}
                  <span>{phraseToText(branch.userInput.referenceText)}</span>
                </div>
                {recognizedTextPairs.has(branch.id) &&
                (<span className={classes.recognizedTextPairs}>&#126;
                  {recognizedTextPairs.get(branch.id)}</span>)}
              </div>
            ))}

          {!customScore && isFail && (
            <>
              {t("components.SessionUserInputActionDisplay.correctChoices")}
              {action.branches
                .filter((branch) => branch.state === ChoiceUserInputBranchState.CORRECT)
                .map((branch) => (
                  <span key={branch.id}>{phraseToText(branch.userInput.referenceText)}</span>
                ))}
            </>
          )}
        </span>
      );
    }
  }, [action, selectedBranches, result.passed, customScore, result.isSkipped, recognizedTextPairs, t]);

  const preText = useMemo(() => {
    if (isUserInputAction(action)) {
      if (action.freeSpeech) {
        return t("components.SessionUserInputActionDisplay.freeSpeech")
      }
    }
  }, [action, t])

  const time = useMemo(() => minutesFromDeltaTimestamp(result.duration, true), [result.duration])
  const after = useMemo(() => {
      return (
        <>
          {time}
          {result.audio && !noSpeech && (
            <div tabIndex={0} className={classes.playBtn} onClick={onPlayClick}>
              <PlayCircleIcon/>
            </div>
          )}
        </>
      )
  }, [onPlayClick, time, result, noSpeech]);

  const speechSpeed = result.speechSpeed
  const pitchRange = result.pitchRange

  const detailedAttempts = result.detailedAttempts

  const [expanded, setExpanded] = useState<boolean>(false);

  const emotions = useMemo(() => {
    const emotionValues = getActionEmotionsData(result, t);

    return (
      emotionValues.length !== 0 && (
        <div>
          {emotionValues.map((value, i) => (
            <div key={i}>{value.label + ": " + (value.score * 100).toFixed(1).toString() + "%"}</div>
          ))}
        </div>
      )
    );
  }, [result, t]);

  const onExpandDetails = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (expanded) {
      setExpanded(false)
    } else {
      setExpanded(true);
    }
  }, [expanded])

  const resultDetails = useMemo(() => {
    const reasons: Record<string, labelProps> = {};

    if ((result.attempts > 1) || !result.passed) {
      reasons.attempts = {
        text: ((isSkipped, attempts) => {
          if (isSkipped) {
            return attempts === 0
              ? t("components.SessionUserInputActionDisplay.failReasons.skippedWithoutAttempts")
              : t("components.SessionUserInputActionDisplay.failReasons.skippedAfterManyAttempts", {attempts})
          } else {
            if (customScore) {
              return "";
            }
            if (attempts < 2) {
              if (
                isChoiceUserInputAction(action) &&
                action.isMultipleChoice &&
                !selectedBranches.find((branch) => branch.state !== ChoiceUserInputBranchState.CORRECT)
              )
                return t("components.SessionUserInputActionDisplay.failReasons.notAllCorrectChoices");
              return t("components.SessionUserInputActionDisplay.failReasons.wrongChoice");
            } else {
              return t("components.SessionUserInputActionDisplay.failReasons.tooManyAttempts", {attempts});
            }
          }
        })(result.isSkipped, result.attempts),
        icon: customScore ? undefined : result.passed ? <CheckmarkIcon/> : <CrossIcon/>
      }
      // Hack for skippedAfterManyAttempts/tooManyAttempts plurals parsing
      t("components.SessionUserInputActionDisplay.attempts", {count: result.attempts})
    }

    if (result.usedHint && !customScore) {
      reasons.usedHint = {
        text: t("components.SessionUserInputActionDisplay.failReasons.usedHint"),
        icon: <KeyIcon/>
      }
    }

    if (speechSpeed) {
      reasons["speechSpeed"] = {
        variant: speechSpeed === SpeechSpeed.REASONABLE ? "success" : "warning",
        text: ((speed) => {
          switch (speed) {
            case SpeechSpeed.FAST:
              return t("components.SessionUserInputActionDisplay.tooFast")
            case SpeechSpeed.SLOW:
              return t("components.SessionUserInputActionDisplay.tooSlow")
            case SpeechSpeed.REASONABLE:
              return t("components.SessionUserInputActionDisplay.speedReasonable")
            default:
              return "[NotImplemented]";
          }
        })(speechSpeed),
        icon: speechSpeed === SpeechSpeed.REASONABLE ? <CheckmarkIcon/> : <AlertCircleOutlineIcon/>
      }
    }

    if (pitchRange) {
      reasons["pitchRange"] = {
        variant: pitchRange === PitchRange.REASONABLE ? "success" : "warning",
        text: ((pitchRange) => {
          switch (pitchRange) {
            case PitchRange.MONOTONIC:
              return t("components.SessionUserInputActionDisplay.tooMonotonic")
            case PitchRange.EXPRESSIVE:
              return t("components.SessionUserInputActionDisplay.tooExpressive")
            case PitchRange.REASONABLE:
              return t("components.SessionUserInputActionDisplay.pitchReasonable")
            default:
              return "[NotImplemented]";
          }
        })(pitchRange),
        icon: pitchRange === PitchRange.REASONABLE ? <CheckmarkIcon/> : <AlertCircleOutlineIcon/>
      }
    }

    if (detailedAttempts.passed + detailedAttempts.failed !== result.attempts)
      reasons.detailedAttempts = {
        text: (() => {
          return t("components.SessionUserInputActionDisplay.detailedAttempts.text", {
            all: detailedAttempts.passed + detailedAttempts.failed,
            passed: detailedAttempts.passed,
            failed: detailedAttempts.failed,
          });

        })(),
        icon: undefined,
      };

      // Hack for detailedAttempts plurals parsing
      t("components.SessionUserInputActionDisplay.detailedAttempts.all", {
        count: detailedAttempts.passed + detailedAttempts.failed,
      });
      t("components.SessionUserInputActionDisplay.detailedAttempts.passed", {count: detailedAttempts.passed});
      t("components.SessionUserInputActionDisplay.detailedAttempts.failed", {count: detailedAttempts.failed});

    return reasons
  }, [result, customScore, pitchRange, speechSpeed, action, selectedBranches, detailedAttempts, t])

  return (
    <SessionActionDisplay variant="user" icon={icon} after={after} {...props}>
      {preText && <span className={classes.preText}>{preText}</span>}
      {mainText}
      {subItem}
      {resultDetails && (
        <div className={classes.labelsWrapper}>
          <div className={classes.column}>
            {resultDetails.attempts && <LabelsRow variant="failed" {...resultDetails.attempts} />}
            {resultDetails.detailedAttempts && (
              <LabelsRow variant="info" className={classes.small} {...resultDetails.detailedAttempts} />
            )}
          </div>
          <div className={classes.column}>
            {resultDetails.isSkipped && <LabelsRow variant="failed" {...resultDetails.isSkipped} />}
            {resultDetails.usedHint && <LabelsRow variant="failed" {...resultDetails.usedHint} />}
            {resultDetails.speechSpeed && !noSpeech && <LabelsRow {...resultDetails.speechSpeed} />}
            {resultDetails.pitchRange && !noSpeech && <LabelsRow {...resultDetails.pitchRange} />}
          </div>
        </div>
      )}
      {emotions && (
        <>
          <div className={classes.emotionsWrapper}>
            <>{t("components.emotions.probableEmotions")}</>
            <IconButton
              className={cn(classes.toggleButton, {[classes.checked]: expanded})}
              size="s"
              icon={CaretDown}
              onClick={onExpandDetails}
            />

            <WithTooltip className={classes.help} as="span" helpText={t("components.emotions.helpText")}>
              <HelpCircleIcon />
            </WithTooltip>
          </div>

          <div className={expanded ? classes.expanded : undefined}>{expanded && emotions}</div>
        </>
      )}
    </SessionActionDisplay>
  );
}

const LabelsRow = ({icon, variant, text, className, ...props}: labelProps) => (
  <div
    className={
      cn(classes.label, classes[variant ?? "warning"], className)}
    {...props}>
    {icon}
    <span className={classes.labelText}>{text}</span>
  </div>
)

SessionUserInputActionDisplay.fragments = {
  root: gql`
    fragment SessionUserInputActionDisplay on Action {
      id
      ... on UserInputAction {
        hintText
        referenceText
        questionText
        freeSpeech
      }

      ... on ChoiceUserInputAction {
        id
        isMultipleChoice
        branches {
          id
          state
          userInput {
            id
            referenceText
            questionText
          }
        }
      }
    }
  `,

  actionResult: gql`
    fragment SessionUserInputActionDisplayActionResult on ActionResult {
      passed
      branchId
      branchIds
      audio
      adaptiveInputUuid
      response

      duration
      attempts
      detailedAttempts {
        passed
        failed
      }
      isSkipped
      usedHint
      recognizedText
      recognizedTextPairs

      speechSpeed
      pitchRange

      emotionJoy
      emotionNeutral
      emotionEnthusiasm
      emotionAnger
      emotionSurprise
      emotionSadness
      emotionDisgust
      emotionFear
      emotionShame
      emotionGuilt

      partlyPassed
      freeSpeechMessage
    }
  `,
};

export default SessionUserInputActionDisplay;
